import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { StringParam, useQueryParam } from 'use-query-params'
import secureLocalStorage from 'react-secure-storage'
import StyledIcon from '../../../components/styled-icon'
import { useAuth } from '../../../context/user.context'
import { icons } from '../../../icons'
import CreationForm from './creation-form'
import { CreationDraft, CreationMode, emptyDraft } from './model'
import { Container } from './styles'
import { BackLink, ModeCard, ModeText, ModeTitle, Modes, Page, Radio, Small, Title } from './ui'

function CreateServer() {
  const [t] = useTranslation()
  const navigate = useNavigate()
  const [queryMode, setMode] = useQueryParam('mode', StringParam)
  const mode: CreationMode = queryMode === 'preorder' ? 'preorder' : 'stock'
  const drafts = useRef<Record<CreationMode, CreationDraft>>({ stock: emptyDraft(), preorder: emptyDraft() })
  const [busy, setBusy] = useState(false)
  const saveDraft = useCallback((values: CreationDraft) => { drafts.current[mode] = values }, [mode])
  useEffect(() => {
    // Remove credentials left by the previous localStorage-based wizard.
    secureLocalStorage.removeItem('baremetal.configure')
    secureLocalStorage.removeItem('baremetal.network')
  }, [])
  const modes: { id: CreationMode; title: string; description: string }[] = [
    { id: 'stock', title: t('DEDICATED_IN_STOCK'), description: t('DEDICATED_MODE_STOCK_DESCRIPTION') },
    { id: 'preorder', title: t('DEDICATED_PREORDER'), description: t('DEDICATED_MODE_PREORDER_DESCRIPTION') },
  ]
  return <Container>
    <Page>
      <BackLink type="button" onClick={() => { if (!busy) navigate('/bare-metal') }}>
        <StyledIcon src={icons.Chevron} />
        {t('BARE_METAL')}
      </BackLink>
      <Title>{t('CREATE_NEW_BARE_METAL_SERVER')}</Title>
      <Modes role="radiogroup" aria-label={String(t('DEDICATED_CREATION_MODE'))}>
        {modes.map(item => <ModeCard key={item.id} type="button" role="radio" aria-checked={mode === item.id}
          $active={mode === item.id} disabled={busy && mode !== item.id}
          onClick={() => { if (!busy && mode !== item.id) setMode(item.id === 'stock' ? undefined : item.id) }}>
          <Radio $checked={mode === item.id} aria-hidden="true" />
          <ModeText>
            <ModeTitle>{item.title}</ModeTitle>
            <Small>{item.description}</Small>
          </ModeText>
        </ModeCard>)}
      </Modes>
      <CreationForm key={mode} mode={mode} draft={drafts.current[mode]} saveDraft={saveDraft}
        onPreorder={() => setMode('preorder')} onBusy={setBusy} />
    </Page>
  </Container>
}

export default function CreateBareMetalServer() {
  const { user } = useAuth()
  return <CreateServer key={user?.user?.active_client} />
}
